// NPM packages
import React from 'react';
import { Typography } from '@mui/material';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';

// All other imports
import MarkdownText from 'components/core/MarkdownText';
import { BreadcrumbType, displayBreadcrumbs, getImageElement } from './utils';
import { renderFlexibleContent } from './renderRichText';
import { RichtextStoryblok } from 'types/storyblokTypes';

const Root = styled.div(({ theme }) => {
  return `
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 100%;
    color: white;
    background: linear-gradient( to right,
      ${theme.palette.primary.main} 50%,
      ${theme.palette.primary.light}
    );
  `;
});

const DisplayArea = styled.div(({ theme }) => {
  return `
    min-width: ${theme.maxToolWidth};
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media (max-width: 1200px) {
      flex-direction: column;
      align-items: center;
      min-width: 0;
      width: 100%;
    }
  `;
});

const TextArea = styled.div(({ theme }) => ({
  margin: '1.5rem 0',
  maxWidth: '80%',
  [theme.breakpoints.down('xl')]: {
    maxWidth: '100%',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
}));

const Header = styled(Typography)({
  marginBottom: '1rem',
});

const Description = styled.div({
  width: '100%',
});

const ImageArea = styled.div({
  display: 'flex',
  justifyContent: 'flex-end', // align image to the right
  alignItems: 'center',
});

const SVGImage = styled.img({
  filter: 'brightness(0) invert(1)',
  height: '8rem',
  width: '8rem',
  margin: '1rem 0',
});

export interface GradientBannerProps {
  header: string | RichtextStoryblok;
  description: string;
  image?: string | JSX.Element;
  imageAlt?: string;
  breadcrumbs?: BreadcrumbType[];
  className?: string;
}

function GradientBanner(props: GradientBannerProps): React.ReactElement | null {
  const { header, description, image, imageAlt, breadcrumbs, className } =
    props;
  const imageElement = getImageElement(image, imageAlt, SVGImage);

  return (
    <Root className={className}>
      <DisplayArea>
        <TextArea>
          {breadcrumbs && displayBreadcrumbs(breadcrumbs, '#ffffff')}
          <Header variant="h1">{renderFlexibleContent(header)}</Header>
          <Description>
            <span>{description}</span>
          </Description>
        </TextArea>
        <ImageArea>{imageElement}</ImageArea>
      </DisplayArea>
    </Root>
  );
}

export default observer(GradientBanner);
